import { useController, type Control, type FieldPath, type FieldValues } from "react-hook-form";
import { useTranslation } from "react-i18next";

interface CheckboxInputProps<TFieldValues extends FieldValues = FieldValues> {
  /** The field name in the form */
  name: FieldPath<TFieldValues>;
  /** React Hook Form control object */
  control: Control<TFieldValues>;
  /** Label text for the checkbox */
  label?: string;
  /** Validation rules for the field */
  rules?: Parameters<typeof useController<TFieldValues>>[0]["rules"];
  /** Whether the checkbox is disabled */
  disabled?: boolean;
  /** Additional CSS classes */
  className?: string;
}

export default function CheckboxInput<TFieldValues extends FieldValues = FieldValues>({
  name,
  control,
  label,
  rules,
  disabled,
  className = "",
}: CheckboxInputProps<TFieldValues>) {
  const { t } = useTranslation();
  const {
    field,
    fieldState: { error },
  } = useController<TFieldValues>({
    name,
    control,
    rules,
  });

  return (
    <div className={className}>
      <div className="form-check">
        <input
          {...field}
          id={name}
          type="checkbox"
          className={`form-check-input ${error ? "is-invalid" : ""}`}
          checked={!!field.value}
          onChange={(e) => field.onChange(e.target.checked)}
          disabled={disabled}
        />
        {label && (
          <label
            className="form-check-label"
            htmlFor={name}
            style={{ cursor: disabled ? "not-allowed" : "pointer" }}
          >
            {label}
          </label>
        )}
      </div>
      {error && (
        <div className="invalid-feedback d-block">
          {error.message ? t(error.message) : ""}
        </div>
      )}
    </div>
  );
}

